import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Table, Form, InputGroup, Card, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationTriangle, 
  faSearch, 
  faTimes, 
  faMicrochip, 
  faNetworkWired, 
  faStore,
  faMapMarkerAlt 
} from '@fortawesome/free-solid-svg-icons';
import { Link } from "react-router-dom";

const DevicesInWrongShopsModal = ({ show, onHide, modalData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('shop'); // 'shop', 'type', 'sn', 'ip'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'

  // Reset search when modal opens/closes or data changes
  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setSortBy('shop');
      setSortOrder('asc');
    }
  }, [show, modalData]);

  // Filter and sort devices based on search term
  const filteredAndSortedDevices = modalData.devices 
    ? [...modalData.devices]
        .filter(device => {
          // Filter by search term (case insensitive)
          if (!searchTerm.trim()) return true;
          
          const searchLower = searchTerm.toLowerCase();
          return (
            (device.shop_name && device.shop_name.toLowerCase().includes(searchLower)) ||
            (device.sn && device.sn.toLowerCase().includes(searchLower)) ||
            (device.dev_type && device.dev_type.toLowerCase().includes(searchLower)) ||
            (device.ip && device.ip.toLowerCase().includes(searchLower))
          );
        })
        .sort((a, b) => {
          let compareResult = 0;
          
          switch (sortBy) {
            case 'shop':
              compareResult = (a.shop_name || '').localeCompare(b.shop_name || '', 'pl', { numeric: true });
              break;
            case 'type':
              compareResult = (a.dev_type || '').localeCompare(b.dev_type || '');
              // Secondary sort by shop name
              if (compareResult === 0) {
                compareResult = (a.shop_name || '').localeCompare(b.shop_name || '', 'pl', { numeric: true });
              }
              break;
            case 'sn':
              compareResult = (a.sn || '').localeCompare(b.sn || '');
              break;
            case 'ip':
              // Sort IP addresses numerically
              const ipA = (a.ip || '').split('.').map(num => parseInt(num) || 0);
              const ipB = (b.ip || '').split('.').map(num => parseInt(num) || 0);
              for (let i = 0; i < 4; i++) {
                if (ipA[i] !== ipB[i]) {
                  compareResult = ipA[i] - ipB[i];
                  break;
                }
              }
              break;
            default:
              compareResult = (a.shop_name || '').localeCompare(b.shop_name || '', 'pl', { numeric: true });
          }
          
          return sortOrder === 'asc' ? compareResult : -compareResult;
        })
    : [];

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Handle sorting change
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Get sort icon for column headers
  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Group devices by shop for summary
  const devicesByShop = filteredAndSortedDevices.reduce((acc, device) => {
    const shopName = device.shop_name || 'Nieznany sklep';
    if (!acc[shopName]) {
      acc[shopName] = [];
    }
    acc[shopName].push(device);
    return acc;
  }, {});

  // Get unique device types for statistics
  const deviceTypeStats = filteredAndSortedDevices.reduce((acc, device) => {
    const type = device.dev_type || 'Nieznany';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton className="bg-warning text-dark">
        <Modal.Title>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {modalData.title || 'Urządzenia w niewłaściwych drogeriach'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modalData.devices && modalData.devices.length > 0 ? (
          <div>
            {/* Summary Cards */}
            <Row className="mb-4">
              <Col md={4}>
                <Card className="border-warning">
                  <Card.Body className="text-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-warning mb-2" />
                    <h4 className="text-warning">{modalData.devices.length}</h4>
                    <small className="text-muted">Problematyczne urządzenia</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-info">
                  <Card.Body className="text-center">
                    <FontAwesomeIcon icon={faStore} size="2x" className="text-info mb-2" />
                    <h4 className="text-info">{Object.keys(devicesByShop).length}</h4>
                    <small className="text-muted">Dotkniętych drogerii</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-secondary">
                  <Card.Body className="text-center">
                    <FontAwesomeIcon icon={faMicrochip} size="2x" className="text-secondary mb-2" />
                    <h4 className="text-secondary">{Object.keys(deviceTypeStats).length}</h4>
                    <small className="text-muted">Typów urządzeń</small>
                  </Card.Body>
                </Card>
              </Col>
              
            </Row>

            {/* Device Type Statistics */}
            {Object.keys(deviceTypeStats).length > 0 && (
              <div className="mb-3">
                <h6 className="text-muted mb-2">Statystyki typów urządzeń:</h6>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(deviceTypeStats).map(([type, count]) => (
                    <Badge key={type} bg="secondary" className="fs-6">
                      {type}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Search Input */}
            <div className="mb-3">
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Wpisz numer drogerii, numer seryjny, typ urządzenia lub IP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
                {searchTerm && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={clearSearch}
                    style={{ borderLeft: 'none' }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
              {searchTerm && filteredAndSortedDevices.length === 0 && (
                <small className="text-muted mt-1 d-block">
                  Brak urządzeń pasujących do wyszukiwanego terminu "{searchTerm}"
                </small>
              )}
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">
                  Sortowanie: {
                    sortBy === 'shop' ? 'Drogeria' :
                    sortBy === 'type' ? 'Typ urządzenia' :
                    sortBy === 'sn' ? 'Numer seryjny' :
                    sortBy === 'ip' ? 'Adres IP' : 'Domyślne'
                  } ({sortOrder === 'asc' ? 'rosnąco' : 'malejąco'})
                </small>
                <small className="text-muted">
                  Kliknij nagłówek kolumny aby zmienić sortowanie
                </small>
              </div>
            </div>
            
            {/* Devices Table */}
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table striped hover responsive size="sm">
                <thead className="sticky-top bg-white">
                  <tr>
                    <th 
                      width="25%" 
                      className="sortable-header"
                      style={{cursor: 'pointer', userSelect: 'none'}}
                      onClick={() => handleSort('shop')}
                      title="Kliknij aby sortować"
                    >
                      <FontAwesomeIcon icon={faStore} className="me-1" />
                      Drogeria {getSortIcon('shop')}
                    </th>
                    <th 
                      width="15%" 
                      className="sortable-header"
                      style={{cursor: 'pointer', userSelect: 'none'}}
                      onClick={() => handleSort('type')}
                      title="Kliknij aby sortować"
                    >
                      <FontAwesomeIcon icon={faMicrochip} className="me-1" />
                      Typ urządzenia {getSortIcon('type')}
                    </th>
                    <th 
                      width="20%" 
                      className="sortable-header"
                      style={{cursor: 'pointer', userSelect: 'none'}}
                      onClick={() => handleSort('sn')}
                      title="Kliknij aby sortować"
                    >
                      Numer seryjny {getSortIcon('sn')}
                    </th>
                    <th 
                      width="15%" 
                      className="sortable-header"
                      style={{cursor: 'pointer', userSelect: 'none'}}
                      onClick={() => handleSort('ip')}
                      title="Kliknij aby sortować"
                    >
                      <FontAwesomeIcon icon={faNetworkWired} className="me-1" />
                      Adres IP {getSortIcon('ip')}
                    </th>
                   
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedDevices.map((device, index) => (
                    <tr key={index}>
                      <td>
                        <Link 
                          to={`/shopview/name/${device.shop_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fw-bold text-decoration-none"
                        >
                          {device.shop_name}
                        </Link>
                      </td>
                      <td>
                        <Badge bg="secondary" className="text-wrap">
                          {device.dev_type}
                        </Badge>
                      </td>
                      <td>
                        <code className="small">{device.sn}</code>
                      </td>
                      <td>
                        <span className="font-monospace small">{device.ip}</span>
                      </td>
                      
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            
          </div>
        ) : (
          <div className="text-center p-4 text-muted">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
            <p className="mb-0">Brak urządzeń w niewłaściwych lokalizacjach</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted small">
            {filteredAndSortedDevices.length > 0 && (
              <>Pokazano {filteredAndSortedDevices.length} z {modalData.devices?.length || 0} urządzeń</>
            )}
          </div>
          <Button variant="secondary" onClick={onHide}>
            Zamknij
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DevicesInWrongShopsModal;